import MainLayout from 'core/layout/MainLayout';
import { HttpService } from 'core/services/http';
import { Utils } from 'core/utils';
import { UseCompanyDetail, UseJobList } from 'cms/features/feature_job/hooks';
import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
    Box,
    Container,
    Typography,
    Chip,
    CircularProgress,
    Stack,
    Paper,
    Grid
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ModernJobCard from '../components/ModernJobCard';

const formatText = (value, fallback = '—') => {
    if (Utils.normalizeText) {
        return Utils.normalizeText(value, fallback);
    }
    if (value === null || value === undefined || value === '') return fallback;
    return value;
};

const getCountryLabel = (country) => {
    if (!country) return '';
    if (typeof country === 'object') {
        return country.title ?? country.name ?? '';
    }
    return country;
};

const getCompanyLogo = (company) => {
    if (company?.image) {
        if (company.image.startsWith('http')) {
            return company.image;
        }
        return `${HttpService.SiteBase}${company.image}`;
    }
    return '/assets/images/jlogo.png';
};

const PREFERENCE_KEY = 'company_jobs_modern_style';

const CompanyDetailsPage = () => {
    const { id } = useParams();
    const { data: company, isLoading } = UseCompanyDetail(id);
    const { data: companyJobs, isLoading: jobsLoading } = UseJobList({ size: 50, filters: { company: id, archived: '0' } });
    const jobs = companyJobs?.results ?? [];
    
    // Save preference to localStorage (always modern style)
    useEffect(() => {
        localStorage.setItem(PREFERENCE_KEY, 'true');
    }, []);

    if (isLoading || !company) {
        return (
            <MainLayout>
                <section className="section-box mt-80">
                    <div className="container text-center py-100">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                </section>
            </MainLayout>
        );
    }

    const jobsCount = company.jobs_count ?? jobs.length;
    const locationLabel = [formatText(company.city, ''), formatText(getCountryLabel(company.country), '')]
        .filter(Boolean)
        .join(', ') || 'Remote';

    return (
        <MainLayout>
            <Box sx={{ py: 3, background: 'white' }}>
                <Container maxWidth="lg">
                    {/* Compact Company Header */}
                    <Paper 
                        elevation={0}
                        sx={{ 
                            p: 2.5, 
                            mb: 3,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 2
                        }}
                    >
                        <Stack direction="row" spacing={2} alignItems="center">
                            <Box
                                component="img"
                                src={getCompanyLogo(company)}
                                alt={company.title}
                                sx={{
                                    width: 60,
                                    height: 60,
                                    borderRadius: 1,
                                    objectFit: 'cover',
                                    flexShrink: 0
                                }}
                            />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="h5" fontWeight={600} gutterBottom sx={{ mb: 0.5 }}>
                                    {formatText(company.title)}
                                </Typography>
                                <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ gap: 1, alignItems: 'center' }}>
                                    <Typography variant="body2" color="text.secondary">
                                        <LocationOnIcon sx={{ fontSize: '16px', verticalAlign: 'middle', mr: 0.5 }} />
                                        {locationLabel}
                                    </Typography>
                                    {company.website && (
                                        <Typography 
                                            variant="body2" 
                                            component="a"
                                            href={company.website.startsWith('http') ? company.website : `https://${company.website}`}
                                            target="_blank"
                                            rel="noreferrer"
                                            sx={{ 
                                                color: 'primary.main',
                                                textDecoration: 'none',
                                                '&:hover': { textDecoration: 'underline' }
                                            }}
                                        >
                                            {company.website}
                                        </Typography>
                                    )}
                                </Stack>
                            </Box>
                            <Chip
                                label={`${jobsCount} Open Job${jobsCount === 1 ? '' : 's'}`}
                                color="success"
                                variant="outlined"
                                sx={{ flexShrink: 0 }}
                            />
                        </Stack>
                    </Paper>

                    {/* Compact Info Section */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} md={8}>
                            <Paper 
                                elevation={0}
                                sx={{ 
                                    p: 2.5,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 2,
                                    height: '100%'
                                }}
                            >
                                <Typography variant="h6" fontWeight={600} gutterBottom sx={{ mb: 1.5 }}>
                                    About {formatText(company.title)}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    {formatText(company.description || 'This company has not added a description yet.')}
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                            Headquarters
                                        </Typography>
                                        <Typography variant="body2">{locationLabel}</Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                            Employees
                                        </Typography>
                                        <Typography variant="body2">{company.number_of_employees || 'Not specified'}</Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                            Contact
                                        </Typography>
                                        <Typography variant="body2">
                                            {formatText(company.phone_number || company.manager_name || 'Not available')}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Paper 
                                elevation={0}
                                sx={{ 
                                    p: 2.5,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 2,
                                    height: '100%'
                                }}
                            >
                                <Typography variant="h6" fontWeight={600} gutterBottom sx={{ mb: 1.5 }}>
                                    Information
                                </Typography>
                                <Stack spacing={1.5}>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                            Founded
                                        </Typography>
                                        <Typography variant="body2">
                                            {company.created_at ? Utils.getDate(company.created_at) : '—'}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                            Manager
                                        </Typography>
                                        <Typography variant="body2">
                                            {formatText(company.manager_name || '—')}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                            VAT number
                                        </Typography>
                                        <Typography variant="body2">
                                            {formatText(company.vat_number || '—')}
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Paper>
                        </Grid>
                    </Grid>

                    <Box sx={{ mt: 4 }}>
                        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                            <Typography variant="h6" fontWeight={600}>
                                Open positions
                            </Typography>
                            <Chip 
                                label={jobsCount} 
                                size="small" 
                                color="primary" 
                                variant="outlined"
                            />
                        </Stack>
                        
                        {jobsLoading && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                <CircularProgress />
                            </Box>
                        )}
                        
                        {!jobsLoading && jobs.length === 0 && (
                            <Box sx={{ py: 4, textAlign: 'center' }}>
                                <Typography variant="body1" color="text.secondary">
                                    No active roles at the moment.
                                </Typography>
                            </Box>
                        )}
                        
                        {!jobsLoading && jobs.length > 0 && (
                            <Box>
                                {jobs.map((job) => (
                                    <ModernJobCard key={job.id} job={job} />
                                ))}
                            </Box>
                        )}
                    </Box>
                </Container>
            </Box>
        </MainLayout>
    );
};

export default CompanyDetailsPage;

